import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { trackFeatureUsage, trackFormSubmission, trackEvent } from '../utils/analytics';

const TrainingVideos = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('ADMIN');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVideo, setNewVideo] = useState({
    title: '',
    titleHindi: '',
    description: '',
    descriptionHindi: '',
    videoUrl: '',
    videoUrlHindi: '',
    targetAudience: 'ADMIN'
  });
  const [editingVideo, setEditingVideo] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');

  const tabs = [
    { key: 'ADMIN', label: 'Admin Videos', icon: '🔧' },
    { key: 'COACH', label: 'Coach Videos', icon: '👨‍🏫' },
    { key: 'CUSTOMER', label: 'Customer Videos', icon: '👤' }
  ];

  useEffect(() => {
    fetchVideos();
    trackFeatureUsage('Training Videos Management', 'view');
  }, [activeTab]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/training-videos/audience/${activeTab}`);
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch training videos:', error);
      setError('Failed to load training videos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const videoData = {
        ...newVideo,
        targetAudience: activeTab
      };

      await axios.post('/api/training-videos', videoData);
      
      trackFormSubmission('add_training_video_form', true);
      trackEvent('training_video_added', {
        event_category: 'Training Video Management',
        event_label: newVideo.title,
        target_audience: activeTab
      });
      
      setShowAddForm(false);
      setConfirmMessage('Training video added successfully!');
      setShowConfirmModal(true);
      resetForm();
      fetchVideos();
    } catch (error) {
      console.error('Failed to add training video:', error);
      trackFormSubmission('add_training_video_form', false);
      setFormError(error.response?.data?.message || 'Failed to add training video. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this training video?')) {
      return;
    }

    try {
      await axios.delete(`/api/training-videos/${videoId}`);
      setConfirmMessage('Training video deleted successfully!');
      setShowConfirmModal(true);
      fetchVideos();
    } catch (error) {
      console.error('Failed to delete training video:', error);
      setError('Failed to delete training video. Please try again.');
    }
  };

  const resetForm = () => {
    setNewVideo({
      title: '',
      titleHindi: '',
      description: '',
      descriptionHindi: '',
      videoUrl: '',
      videoUrlHindi: '',
      targetAudience: activeTab
    });
  };

  const handleEditVideo = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const videoData = {
        ...newVideo,
        targetAudience: activeTab
      };

      await axios.put(`/api/training-videos/${editingVideo.id}`, videoData);
      
      trackFormSubmission('edit_training_video_form', true);
      trackEvent('training_video_updated', {
        event_category: 'Training Video Management',
        event_label: newVideo.title,
        target_audience: activeTab
      });
      
      setShowEditForm(false);
      setEditingVideo(null);
      setConfirmMessage('Training video updated successfully!');
      setShowConfirmModal(true);
      resetForm();
      fetchVideos();
    } catch (error) {
      console.error('Failed to update training video:', error);
      trackFormSubmission('edit_training_video_form', false);
      setFormError(error.response?.data?.message || 'Failed to update training video. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const openEditForm = (video) => {
    setEditingVideo(video);
    setNewVideo({
      title: video.title || '',
      titleHindi: video.titleHindi || '',
      description: video.description || '',
      descriptionHindi: video.descriptionHindi || '',
      videoUrl: video.videoUrl || '',
      videoUrlHindi: video.videoUrlHindi || '',
      targetAudience: video.targetAudience
    });
    setShowEditForm(true);
  };

  const extractYouTubeVideoId = (url) => {
    try {
      if (url.includes('youtube.com/watch?v=')) {
        return url.split('v=')[1].split('&')[0];
      } else if (url.includes('youtu.be/')) {
        return url.split('youtu.be/')[1].split('?')[0];
      } else if (url.includes('youtube.com/embed/')) {
        return url.split('embed/')[1].split('?')[0];
      }
    } catch (error) {
      console.error('Failed to extract YouTube video ID:', error);
    }
    return null;
  };

  const getVideoThumbnail = (videoUrl) => {
    const videoId = extractYouTubeVideoId(videoUrl);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    return null;
  };

  return (
    <div className="page">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <h1 className="page-title" style={{ margin: 0 }}>Training Videos Management</h1>
          <div style={{ 
            fontSize: '14px', 
            color: 'var(--gray-dark)', 
            marginTop: '5px'
          }}>
            Manage training videos for different user types
          </div>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowAddForm(true);
          }}
          style={{ flexShrink: 0 }}
        >
          <span className="btn-icon">➕</span>
          <span className="btn-text">Add Training Video</span>
        </button>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {/* Tab Navigation */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ 
          display: 'flex', 
          borderBottom: '2px solid var(--gray-medium)', 
          marginBottom: '0' 
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: activeTab === tab.key ? 'var(--primary-blue)' : 'transparent',
                color: activeTab === tab.key ? 'var(--white)' : 'var(--gray-dark)',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontWeight: '500',
                marginRight: '5px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Videos Grid */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            {tabs.find(tab => tab.key === activeTab)?.label} ({videos.length} videos)
          </h3>
        </div>

        {loading ? (
          <div className="loading">Loading training videos...</div>
        ) : videos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
            No training videos found for {activeTab.toLowerCase()} users. Click "Add Training Video" to create the first video.
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
            gap: '20px'
          }}>
            {videos.map((video) => (
              <div 
                key={video.id}
                style={{
                  border: '1px solid var(--gray-medium)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: 'var(--white)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
              >
                {/* Video Thumbnail */}
                <div style={{ 
                  position: 'relative', 
                  paddingBottom: '56.25%', 
                  height: 0,
                  background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--dark-blue) 100%)',
                  cursor: 'pointer'
                }}
                onClick={() => window.open(video.videoUrl, '_blank')}
                >
                  {getVideoThumbnail(video.videoUrl) ? (
                    <img 
                      src={getVideoThumbnail(video.videoUrl)}
                      alt="Video thumbnail"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: getVideoThumbnail(video.videoUrl) ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--dark-blue) 100%)',
                    fontSize: '48px',
                    color: 'white'
                  }}>
                    🎥
                  </div>
                  
                  {/* Play Button Overlay */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '70px',
                    height: '70px',
                    background: 'rgba(0, 0, 0, 0.8)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    color: 'white',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(0, 0, 0, 0.9)';
                    e.target.style.transform = 'translate(-50%, -50%) scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(0, 0, 0, 0.8)';
                    e.target.style.transform = 'translate(-50%, -50%) scale(1)';
                  }}
                  >
                    ▶️
                  </div>
                  
                  {/* Training Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: 'var(--success)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 'bold'
                  }}>
                    📚 TRAINING
                  </div>
                </div>
                
                {/* Video Info */}
                <div style={{ padding: '20px' }}>
                  <div style={{ 
                    fontWeight: '600', 
                    marginBottom: '10px',
                    fontSize: '16px',
                    lineHeight: '1.4',
                    color: 'var(--dark-blue)'
                  }}>
                    {video.title}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: 'var(--gray-dark)',
                    marginBottom: '15px',
                    lineHeight: '1.5'
                  }}>
                    {video.description}
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'var(--gray-dark)',
                    marginBottom: '15px'
                  }}>
                    Added by {video.createdBy} • {new Date(video.createdAt).toLocaleDateString()}
                  </div>
                  
                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                    <button 
                      className="btn btn-primary"
                      style={{ 
                        fontSize: '12px', 
                        padding: '8px 16px',
                        flex: 1,
                        background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--dark-blue) 100%)',
                        border: 'none'
                      }}
                      onClick={() => window.open(video.videoUrl, '_blank')}
                      title="Watch Video"
                    >
                      🎥 Watch
                    </button>
                    <button 
                      className="btn btn-secondary"
                      style={{ fontSize: '11px', padding: '6px 8px', marginRight: '4px' }}
                      onClick={() => openEditForm(video)}
                      title="Edit Video"
                    >
                      ✏️
                    </button>
                    <button 
                      className="btn btn-secondary"
                      style={{ fontSize: '11px', padding: '6px 8px', color: 'var(--error)' }}
                      onClick={() => handleDeleteVideo(video.id)}
                      title="Delete Video"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Video Form Modal */}
      {showAddForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              Add Training Video for {tabs.find(tab => tab.key === activeTab)?.label}
            </h3>

            {formError && (
              <div className="alert alert-error">{formError}</div>
            )}

            <form onSubmit={handleAddVideo}>
              {/* English Content */}
              <div style={{ marginBottom: '25px', padding: '20px', border: '2px solid var(--primary-blue)', borderRadius: '8px' }}>
                <h4 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>🇬🇧 English Content</h4>
                
                <div className="form-group">
                  <label htmlFor="title" className="form-label">
                    Video Title (English) *
                  </label>
                  <input
                    type="text"
                    id="title"
                    className="form-input"
                    value={newVideo.title}
                    onChange={(e) => setNewVideo({...newVideo, title: e.target.value})}
                    required
                    disabled={formLoading}
                    placeholder="e.g., How to Add Customers"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description" className="form-label">
                    Description (English) *
                  </label>
                  <textarea
                    id="description"
                    className="form-input"
                    rows="3"
                    value={newVideo.description}
                    onChange={(e) => setNewVideo({...newVideo, description: e.target.value})}
                    required
                    disabled={formLoading}
                    placeholder="Brief description of what this video teaches..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="videoUrl" className="form-label">
                    YouTube Video URL (English) *
                  </label>
                  <input
                    type="url"
                    id="videoUrl"
                    className="form-input"
                    value={newVideo.videoUrl}
                    onChange={(e) => setNewVideo({...newVideo, videoUrl: e.target.value})}
                    required
                    disabled={formLoading}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
              </div>

              {/* Hindi Content */}
              <div style={{ marginBottom: '25px', padding: '20px', border: '2px solid var(--success)', borderRadius: '8px' }}>
                <h4 style={{ color: 'var(--success)', marginBottom: '15px' }}>🇮🇳 Hindi Content (Optional)</h4>
                
                <div className="form-group">
                  <label htmlFor="titleHindi" className="form-label">
                    Video Title (Hindi)
                  </label>
                  <input
                    type="text"
                    id="titleHindi"
                    className="form-input"
                    value={newVideo.titleHindi}
                    onChange={(e) => setNewVideo({...newVideo, titleHindi: e.target.value})}
                    disabled={formLoading}
                    placeholder="ग्राहकों को कैसे जोड़ें"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="descriptionHindi" className="form-label">
                    Description (Hindi)
                  </label>
                  <textarea
                    id="descriptionHindi"
                    className="form-input"
                    rows="3"
                    value={newVideo.descriptionHindi}
                    onChange={(e) => setNewVideo({...newVideo, descriptionHindi: e.target.value})}
                    disabled={formLoading}
                    placeholder="इस वीडियो में क्या सिखाया गया है इसका संक्षिप्त विवरण..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="videoUrlHindi" className="form-label">
                    YouTube Video URL (Hindi)
                  </label>
                  <input
                    type="url"
                    id="videoUrlHindi"
                    className="form-input"
                    value={newVideo.videoUrlHindi}
                    onChange={(e) => setNewVideo({...newVideo, videoUrlHindi: e.target.value})}
                    disabled={formLoading}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
              </div>

              {/* Video Preview */}
              {newVideo.videoUrl && getVideoThumbnail(newVideo.videoUrl) && (
                <div style={{ marginBottom: '20px' }}>
                  <label className="form-label">English Video Preview:</label>
                  <div style={{
                    border: '1px solid var(--gray-medium)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    maxWidth: '300px'
                  }}>
                    <img 
                      src={getVideoThumbnail(newVideo.videoUrl)}
                      alt="Video preview"
                      style={{ 
                        width: '100%',
                        height: 'auto',
                        display: 'block'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setFormError('');
                    resetForm();
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={formLoading}
                >
                  {formLoading ? 'Adding...' : 'Add Video'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Video Form Modal */}
      {showEditForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              Edit Training Video: {editingVideo?.title}
            </h3>

            {formError && (
              <div className="alert alert-error">{formError}</div>
            )}

            <form onSubmit={handleEditVideo}>
              {/* English Content */}
              <div style={{ marginBottom: '25px', padding: '20px', border: '2px solid var(--primary-blue)', borderRadius: '8px' }}>
                <h4 style={{ color: 'var(--primary-blue)', marginBottom: '15px' }}>🇬🇧 English Content</h4>
                
                <div className="form-group">
                  <label htmlFor="editTitle" className="form-label">
                    Video Title (English) *
                  </label>
                  <input
                    type="text"
                    id="editTitle"
                    className="form-input"
                    value={newVideo.title}
                    onChange={(e) => setNewVideo({...newVideo, title: e.target.value})}
                    required
                    disabled={formLoading}
                    placeholder="e.g., How to Add Customers"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="editDescription" className="form-label">
                    Description (English) *
                  </label>
                  <textarea
                    id="editDescription"
                    className="form-input"
                    rows="3"
                    value={newVideo.description}
                    onChange={(e) => setNewVideo({...newVideo, description: e.target.value})}
                    required
                    disabled={formLoading}
                    placeholder="Brief description of what this video teaches..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="editVideoUrl" className="form-label">
                    YouTube Video URL (English) *
                  </label>
                  <input
                    type="url"
                    id="editVideoUrl"
                    className="form-input"
                    value={newVideo.videoUrl}
                    onChange={(e) => setNewVideo({...newVideo, videoUrl: e.target.value})}
                    required
                    disabled={formLoading}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
              </div>

              {/* Hindi Content */}
              <div style={{ marginBottom: '25px', padding: '20px', border: '2px solid var(--success)', borderRadius: '8px' }}>
                <h4 style={{ color: 'var(--success)', marginBottom: '15px' }}>🇮🇳 Hindi Content (Optional)</h4>
                
                <div className="form-group">
                  <label htmlFor="editTitleHindi" className="form-label">
                    Video Title (Hindi)
                  </label>
                  <input
                    type="text"
                    id="editTitleHindi"
                    className="form-input"
                    value={newVideo.titleHindi}
                    onChange={(e) => setNewVideo({...newVideo, titleHindi: e.target.value})}
                    disabled={formLoading}
                    placeholder="ग्राहकों को कैसे जोड़ें"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="editDescriptionHindi" className="form-label">
                    Description (Hindi)
                  </label>
                  <textarea
                    id="editDescriptionHindi"
                    className="form-input"
                    rows="3"
                    value={newVideo.descriptionHindi}
                    onChange={(e) => setNewVideo({...newVideo, descriptionHindi: e.target.value})}
                    disabled={formLoading}
                    placeholder="इस वीडियो में क्या सिखाया गया है इसका संक्षिप्त विवरण..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="editVideoUrlHindi" className="form-label">
                    YouTube Video URL (Hindi)
                  </label>
                  <input
                    type="url"
                    id="editVideoUrlHindi"
                    className="form-input"
                    value={newVideo.videoUrlHindi}
                    onChange={(e) => setNewVideo({...newVideo, videoUrlHindi: e.target.value})}
                    disabled={formLoading}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditForm(false);
                    setEditingVideo(null);
                    setFormError('');
                    resetForm();
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={formLoading}
                >
                  {formLoading ? 'Updating...' : 'Update Video'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
              Success!
            </h3>
            <p style={{ color: 'var(--gray-dark)', marginBottom: '25px' }}>
              {confirmMessage}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowConfirmModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingVideos;
